import React, { useState, useEffect } from 'react';

import { useTheme } from '@mui/joy';

import { useSelector, useDispatch } from 'react-redux';

import lookupTableActions from 'actions/lookupTableActions';
import personActions from 'actions/personActions';
import observationActions from 'actions/observationActions';
import appActions from 'actions/appActions';

import Stack from 'components/core/Stack';
import Typography from 'components/core/Typography';
import Button from 'components/core/Button';
import { CircularProgress } from '@mui/joy';

import QueryBuilder from 'components/QueryBuilder';
import ObservationsTable from 'components/ObservationsTable';

const ObservationQuery = () => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const criteria = useSelector(({ app }) => app.criteria);
    const observationOptions = useSelector(({ lookupTable }) => lookupTable.options);
    const personList = useSelector(({ person }) => person.list);
    const observationList = useSelector(({ observation }) => observation.list);
    const pagination = useSelector(({ observation }) => observation.pagination);
    const isLoading = useSelector(({ observation }) => observation.isLoading);
    const [canRunQuery, setCanRunQuery] = useState(false);
    const [hasRunQuery, setHasRunQuery] = useState(false);
    const [pageSize] = useState(20);

    const setCriteria = (newCriteria) => {
        dispatch(appActions.setCriteria(newCriteria));
    }

    useEffect(() => {
        dispatch(lookupTableActions.fetchAllOptions());
        dispatch(personActions.list());
        dispatch(observationActions.clearList());
        if (criteria.length > 0) {
            setCanRunQuery(true);
            runQuery();
        }
    }, [dispatch]);

    const findLocationChildren = (location, locations) => {
        const children = [];
        locations.forEach(loc => {
            if (loc.get('parent')?.id === location.id) {
                children.push(loc);
                children.push(...findLocationChildren(loc, locations));
            }
        });
        return children;
    }


    const handleLocationAdjustment = (locationCriterion) => {
        const location = locationCriterion.value;
        const operator = locationCriterion.operator;
        const locations = observationOptions.locations
        const locationChildren = findLocationChildren(location, locations);

        return locationChildren.map(child => {
            return {
                field: 'location',
                operator: operator,
                value: child
            }
        });
    }
    const makeAdjustedCriteria = () => {
        const adjustedCriteria = [...criteria]
        const locationCriteria = criteria.filter(c => c.field === 'location');
        if (locationCriteria.length > 0) {
            locationCriteria.forEach(locationCriterion => {
                const locationAdjustments = handleLocationAdjustment(locationCriterion);
                adjustedCriteria.push(...locationAdjustments);
            });
        }

        return adjustedCriteria;
    }

    const runQuery = () => {
        if (criteria.length === 0) return;
        
        const adjustedCriteria = makeAdjustedCriteria();


        dispatch(observationActions.runQuery(adjustedCriteria, 1, pageSize)).then(({value}) => {
            setHasRunQuery(true);
            if (value.data && value.data.length > 0) {
                setCanRunQuery(false);
            }
        });
    };

    const loadMoreResults = () => {
        if (!pagination.hasMore) return;

        const adjustedCriteria = makeAdjustedCriteria();
        dispatch(observationActions.loadMoreObservations(adjustedCriteria, pagination.page, pagination.pageSize));
    };

    const handleCriteriaChange = (newCriteria) => {
        setCriteria(newCriteria);
        setCanRunQuery(newCriteria.length > 0);
        setHasRunQuery(false);
    }

    const renderQueryResults = () => {
        if (!hasRunQuery) return null;
        if (observationList.length === 0) {
            return (
                <Typography variant="h4">
                    No observations found matching the query criteria.
                </Typography>
            );
        }

        return (
            <ObservationsTable
                observations={observationList}
                maxHeight="50vh" 
                showDownloadButton={true}
                onLoadMore={loadMoreResults}
                hasMoreRecords={pagination.hasMore}
                isLoading={isLoading}
                totalCount={pagination.total}
            />
        );
    }

    const hasContradictoryCriteria = criteria.some((c1, i) => 
        criteria.some((c2, j) => 
            i !== j && 
            c1.field === c2.field && 
            ((c1.operator === 'equals' && c2.operator === 'not equals') ||
             (c1.operator === 'not equals' && c2.operator === 'equals')) &&
            JSON.stringify(c1.value) === JSON.stringify(c2.value)
        )
    );

    return (
        <Stack spacing={2}>
            <Typography variant="h2" sx={{ mt: 2, mb: 2 }}>
                Observation Query Builder
            </Typography>
            {criteria.length === 0 && (
                <Typography variant="h4" sx={{ mb: 4 }}>
                    To get started, add a query criteria for the observation(s) you are trying to find.
                </Typography>
            )}
            <QueryBuilder 
                criteria={criteria} 
                setCriteria={handleCriteriaChange} 
                observationOptions={observationOptions}
                personList={personList}
            />
            <Button 
                onClick={runQuery} 
                disabled={!canRunQuery || hasContradictoryCriteria || isLoading}
            >
                {isLoading && !hasRunQuery ? (
                    <CircularProgress size="sm" />
                ) : (
                    'Run Query'
                )}
            </Button>
            {hasContradictoryCriteria && (
                <Typography variant="body" sx={{ mb: 4 }} style={{color: theme.palette.danger[500]}}>
                    This query has contradictory criteria. Please remove the conflicting criteria before running the query.
                </Typography>
            )}
            {renderQueryResults()}
        </Stack>
    );
}

export default ObservationQuery;